/**
 * Currency Formatters
 *
 * Locale-aware currency formatting using Intl.NumberFormat.
 */

import { DEFAULT_CURRENCY } from '../types';

// ═══════════════════════════════════════════════════════════════════════════
// FORMATTERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Format amount as currency
 *
 * @param amount - Amount to format
 * @param locale - Locale string (e.g., 'en-US', 'de-DE')
 * @param currency - Currency code (defaults to EUR)
 *
 * @example
 * formatCurrency(99.99, 'en-US', 'USD') // "$99.99"
 * formatCurrency(99.99, 'de-DE', 'EUR') // "99,99 €"
 */
export function formatCurrency(
  amount: number,
  locale: string,
  currency: string = DEFAULT_CURRENCY
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/**
 * Format currency with compact notation
 *
 * @param amount - Amount to format
 * @param locale - Locale string
 * @param currency - Currency code (defaults to EUR)
 *
 * @example
 * formatCurrencyCompact(1500000, 'en-US', 'USD') // "$1.5M"
 * formatCurrencyCompact(1500000, 'de-DE', 'EUR') // "1,5 Mio. €"
 */
export function formatCurrencyCompact(
  amount: number,
  locale: string,
  currency: string = DEFAULT_CURRENCY
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}
